// プリセット（Heroカード / バンドル / 二択カード）の入力チェック。
//
// **正はバックエンド**（backend/routers/purchase_assist.py の validate_*_preset）。
// ここはサーバーへ往復する前に運用者へ知らせるための先出しで、二重実装ではない。
// 商品IDが実在するかなど DB を見る検査はサーバー側にしか無いため、ここを通っても
// 保存が弾かれることはある（そのときはサーバーが返した文言をそのまま出す）。
//
// 画面（RichUIDesigner.jsx）から切り出してあるのは、テストできるようにするため。

export type PresetForm = {
  name?: string
  priority?: number | null
  trigger_mode?: string
  trigger_keywords?: string[]
  start_at?: string | null
  end_at?: string | null
  // Hero
  product_id?: string
  background_type?: string
  background_image_url?: string
  background_primary_color?: string
  background_secondary_color?: string
  text_color?: string
  // バンドル
  bundle_name?: string
  items?: { product_id?: string }[]
  discount_type?: string
  discount_value?: number | null
  header_color?: string
  // 二択カード
  question?: string
  choices?: { label?: string }[]
  // 共通
  accent_color?: string
}

const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

const checkColor = (value: string | undefined, label: string, errors: string[]) => {
  if (value && !HEX_COLOR.test(value)) {
    errors.push(`${label}は #RGB または #RRGGBB の形式で指定してください`)
  }
}

const checkCommon = (form: PresetForm, errors: string[]) => {
  if (!(form.name || '').trim()) errors.push('プリセット名を入力してください')
  const words = (form.trigger_keywords || []).filter((k) => (k || '').trim())
  // 強制表示なのにキーワードが無いと、永久に出ないプリセットになる
  if ((form.trigger_mode === 'force' || form.trigger_mode === 'both') && words.length === 0) {
    errors.push('トリガー方式が「強制表示」のときは、トリガーキーワードを1つ以上指定してください')
  }
  if (form.start_at && form.end_at && form.start_at >= form.end_at) {
    errors.push('掲載終了日時は開始日時より後にしてください')
  }
  if (form.priority != null && (form.priority < 0 || form.priority > 9999)) {
    errors.push('優先度は 0〜9999 で指定してください')
  }
}

export const validateHeroForm = (form: PresetForm): string[] => {
  const errors: string[] = []
  checkCommon(form, errors)
  if (!(form.product_id || '').trim()) errors.push('商品IDを入力してください')
  // 背景に画像を選んだのにURLが無いと、真っ白なカードになる
  if (form.background_type === 'image' && !(form.background_image_url || '').trim()) {
    errors.push('背景の種類が「画像」のときは背景画像URLを指定してください')
  }
  checkColor(form.background_primary_color, '背景色1', errors)
  checkColor(form.background_secondary_color, '背景色2', errors)
  checkColor(form.accent_color, 'アクセント色', errors)
  checkColor(form.text_color, '文字色', errors)
  return errors
}

export const validateBundleForm = (form: PresetForm): string[] => {
  const errors: string[] = []
  checkCommon(form, errors)
  if (!(form.bundle_name || '').trim()) errors.push('バンドル表示名を入力してください')

  const items = form.items || []
  // バンドルは複数商品のセット。1点以下では「セットで買うと得」が成り立たない
  if (items.length < 2) errors.push('バンドルの商品は2点以上を指定してください')
  if (items.length > 8) errors.push('バンドルの商品は8点以内で指定してください')
  const seen = new Set<string>()
  items.forEach((it, i) => {
    const pid = (it?.product_id || '').trim()
    if (!pid) errors.push(`${i + 1}点目の商品IDを入力してください`)
    else if (seen.has(pid)) errors.push(`商品ID「${pid}」が重複しています`)
    else seen.add(pid)
  })

  if (form.discount_value != null && form.discount_value < 0) {
    errors.push('割引額は0以上で指定してください')
  }
  if (form.discount_type === 'percent' && (form.discount_value ?? 0) > 100) {
    errors.push('割引率は100%以内で指定してください')
  }
  checkColor(form.header_color, 'ヘッダー色', errors)
  checkColor(form.accent_color, 'アクセント色', errors)
  return errors
}

export const validateQuickChoiceForm = (form: PresetForm): string[] => {
  const errors: string[] = []
  checkCommon(form, errors)
  if (!(form.question || '').trim()) errors.push('質問文を入力してください')

  const choices = form.choices || []
  // 二択カードは選ばせるための部品。選択肢が1つでは選ばせられない
  if (choices.length < 2) errors.push('選択肢は2つ以上を指定してください')
  if (choices.length > 6) errors.push('選択肢は6つ以内で指定してください')
  choices.forEach((c, i) => {
    if (!(c?.label || '').trim()) errors.push(`${i + 1}つ目の選択肢のラベルを入力してください`)
  })
  checkColor(form.accent_color, 'アクセント色', errors)
  return errors
}

/** サーバーのエラー本文から detail を取り出す。JSON でなければステータス付きでそのまま返す。 */
export const presetErrorMessage = async (res: Response): Promise<string> => {
  const body = await res.text()
  try {
    return JSON.parse(body).detail || body
  } catch {
    return `HTTP ${res.status}: ${body}`
  }
}
